<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { mapGetters } from 'vuex';
import { useMediaQuery } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { isWhatsAppStyleInbox } from 'dashboard/helper/inbox';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeIndex: 0,
      finishingDailyTraining: false,
      dailyTrainingElapsedSeconds: 0,
      dailyTrainingTimerId: null,
      dailyTrainingFinishRetryTimerId: null,
      dailyTrainingFallbackStartedAt: null,
      dailyTrainingAutoFinishAttempted: false,
    };
  },
  setup() {
    const isMobileViewport = useMediaQuery('(max-width: 767px)');

    return { isMobileViewport };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentAccountId: 'getCurrentAccountId',
      dashboardApps: 'dashboardApps/getRecords',
      currentUser: 'getCurrentUser',
    }),
    dailyTrainingActive() {
      const attrs = this.currentUser?.custom_attributes || {};
      return Boolean(attrs.daily_training_active);
    },
    customTrainingActive() {
      const attrs = this.currentUser?.custom_attributes || {};
      return Boolean(attrs.custom_training_active);
    },
    dailyTrainingConversationId() {
      return Number(
        this.currentUser?.custom_attributes?.daily_training_conversation_id || 0
      );
    },
    isDailyTrainingConversation() {
      return (
        this.dailyTrainingActive &&
        Number(this.currentChat?.id || 0) === this.dailyTrainingConversationId
      );
    },
    isCustomTrainingConversation() {
      return this.isDailyTrainingConversation && this.customTrainingActive;
    },
    canForceFinishDailyTraining() {
      if (this.isCustomTrainingConversation) return false;
      return (
        String(this.currentUser?.email || '')
          .trim()
          .toLowerCase() === 'lucas@skalacode.com'
      );
    },
    trainingBannerTitle() {
      if (!this.isCustomTrainingConversation) {
        return 'Treino do Dia em andamento';
      }
      const title = this.currentUser?.custom_attributes?.daily_training_title;
      return title ? `${title} — teste prático` : 'Teste prático em andamento';
    },
    trainingBannerDescription() {
      return this.isCustomTrainingConversation
        ? 'Atenda o cliente simulado com base no conteúdo do treinamento. As demais conversas ficam bloqueadas até a avaliação.'
        : 'Este é um WhatsApp simulado. As demais conversas ficam bloqueadas até a finalização.';
    },
    trainingFinishLabel() {
      if (this.finishingDailyTraining) {
        return this.isCustomTrainingConversation
          ? 'Avaliando teste…'
          : 'Analisando treino…';
      }
      return this.isCustomTrainingConversation
        ? 'Finalizar teste prático'
        : 'Finalizar treino';
    },
    dailyTrainingMinimumDuration() {
      const duration = Number(
        this.currentUser?.custom_attributes?.daily_training_min_duration || 0
      );
      return Number.isFinite(duration) && duration > 0 ? duration : 0;
    },
    dailyTrainingElapsedLabel() {
      return this.formatTrainingDuration(this.dailyTrainingElapsedSeconds);
    },
    dailyTrainingMinimumLabel() {
      return this.formatTrainingDuration(this.dailyTrainingMinimumDuration);
    },
    dailyTrainingMinimumReached() {
      return (
        this.dailyTrainingMinimumDuration > 0 &&
        this.dailyTrainingElapsedSeconds >= this.dailyTrainingMinimumDuration
      );
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    currentInbox() {
      return this.$store.getters['inboxes/getInbox'](
        this.currentChat?.inbox_id
      );
    },
    isWhatsAppStyleConversation() {
      return isWhatsAppStyleInbox(this.currentInbox);
    },
    isMobileWhatsAppConversation() {
      return this.isMobileViewport || this.isWhatsAppStyleConversation;
    },
  },
  watch: {
    isDailyTrainingConversation: {
      immediate: true,
      handler(isActive) {
        if (isActive) {
          this.startDailyTrainingTimer();
          return;
        }
        this.stopDailyTrainingTimer();
      },
    },
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
      this.clearDailyTrainingFinishRetry();
      this.dailyTrainingFallbackStartedAt = null;
      this.dailyTrainingAutoFinishAttempted = false;
      this.updateDailyTrainingTimer();
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    this.startDailyTrainingTimer();
  },
  beforeUnmount() {
    this.stopDailyTrainingTimer();
  },
  methods: {
    formatTrainingDuration(totalSeconds) {
      const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainder = seconds % 60;
      const parts = [minutes, remainder].map(value =>
        String(value).padStart(2, '0')
      );
      if (hours > 0) {
        parts.unshift(String(hours).padStart(2, '0'));
      }
      return parts.join(':');
    },
    resolveDailyTrainingStartedAt() {
      const configuredStartedAt =
        this.currentUser?.custom_attributes?.daily_training_started_at;
      if (configuredStartedAt) {
        const configuredTimestamp = Date.parse(configuredStartedAt);
        if (Number.isFinite(configuredTimestamp)) {
          return configuredTimestamp;
        }
      }

      // No Treino do Dia o backend publica este atributo somente ao receber a
      // primeira resposta do vendedor. Até lá o relógio deve permanecer zerado.
      if (!this.isCustomTrainingConversation) {
        const firstSellerReply = (this.currentChat?.messages || []).find(
          message =>
            !message?.private &&
            (message?.message_type === 1 ||
              message?.message_type === 'outgoing')
        );
        const replyCreatedAt = firstSellerReply?.created_at;
        if (replyCreatedAt) {
          const numericTimestamp = Number(replyCreatedAt);
          if (Number.isFinite(numericTimestamp)) {
            return numericTimestamp > 1000000000000
              ? numericTimestamp
              : numericTimestamp * 1000;
          }
          const parsedTimestamp = Date.parse(replyCreatedAt);
          if (Number.isFinite(parsedTimestamp)) {
            return parsedTimestamp;
          }
        }

        return null;
      }

      const conversationCreatedAt = this.currentChat?.created_at;
      if (conversationCreatedAt) {
        const numericTimestamp = Number(conversationCreatedAt);
        if (Number.isFinite(numericTimestamp)) {
          return numericTimestamp > 1000000000000
            ? numericTimestamp
            : numericTimestamp * 1000;
        }
        const parsedTimestamp = Date.parse(conversationCreatedAt);
        if (Number.isFinite(parsedTimestamp)) {
          return parsedTimestamp;
        }
      }

      if (!this.dailyTrainingFallbackStartedAt) {
        this.dailyTrainingFallbackStartedAt = Date.now();
      }
      return this.dailyTrainingFallbackStartedAt;
    },
    updateDailyTrainingTimer() {
      if (!this.isDailyTrainingConversation) {
        this.dailyTrainingElapsedSeconds = 0;
        return;
      }
      const startedAt = this.resolveDailyTrainingStartedAt();
      if (!startedAt) {
        this.dailyTrainingElapsedSeconds = 0;
        return;
      }
      this.dailyTrainingElapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - startedAt) / 1000)
      );
      this.finishDailyTrainingWhenTimeIsUp();
    },
    startDailyTrainingTimer() {
      if (!this.isDailyTrainingConversation || this.dailyTrainingTimerId) {
        return;
      }
      this.updateDailyTrainingTimer();
      this.dailyTrainingTimerId = window.setInterval(
        this.updateDailyTrainingTimer,
        1000
      );
    },
    stopDailyTrainingTimer() {
      if (this.dailyTrainingTimerId) {
        window.clearInterval(this.dailyTrainingTimerId);
        this.dailyTrainingTimerId = null;
      }
      this.clearDailyTrainingFinishRetry();
    },
    clearDailyTrainingFinishRetry() {
      if (this.dailyTrainingFinishRetryTimerId) {
        window.clearTimeout(this.dailyTrainingFinishRetryTimerId);
        this.dailyTrainingFinishRetryTimerId = null;
      }
    },
    isDailyTrainingWaitingError(error) {
      const status = error?.response?.status;
      const message = String(
        error?.response?.data?.message || ''
      ).toLowerCase();

      return (
        [409, 425, 429].includes(status) ||
        /(aguard|esper|tempo m[ií]nimo|cedo demais)/i.test(message)
      );
    },
    scheduleDailyTrainingFinishRetry() {
      if (
        this.dailyTrainingFinishRetryTimerId ||
        !this.isDailyTrainingConversation ||
        !this.dailyTrainingMinimumReached
      ) {
        return;
      }

      this.dailyTrainingFinishRetryTimerId = window.setTimeout(() => {
        this.dailyTrainingFinishRetryTimerId = null;
        this.finishDailyTraining();
      }, 5000);
    },
    async finishDailyTraining(force = false) {
      if (this.finishingDailyTraining || !this.isDailyTrainingConversation) {
        return;
      }

      if (force) {
        if (!this.canForceFinishDailyTraining) return;
        const confirmed = window.confirm(
          'Esta ação é exclusiva de Lucas (lucas@skalacode.com) e encerrará o treino sem análise ou recompensa. Deseja liberar o Zoraxy agora?'
        );
        if (!confirmed) return;
      }

      this.finishingDailyTraining = true;
      try {
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/aceleres/daily_training/finish`,
          {
            conversation_id: this.dailyTrainingConversationId,
            force,
          }
        );
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url);
          return;
        }
        useAlert(
          this.isCustomTrainingConversation
            ? 'Teste prático finalizado com sucesso.'
            : 'Treino finalizado com sucesso.'
        );
      } catch (error) {
        if (!force && this.isDailyTrainingWaitingError(error)) {
          this.scheduleDailyTrainingFinishRetry();
          return;
        }

        useAlert(
          error?.response?.data?.message ||
            'Não foi possível finalizar o treino agora.'
        );
      } finally {
        this.finishingDailyTraining = false;
      }
    },
    finishDailyTrainingWhenTimeIsUp() {
      if (
        !this.dailyTrainingMinimumReached ||
        this.dailyTrainingAutoFinishAttempted ||
        this.finishingDailyTraining
      ) {
        return;
      }

      this.dailyTrainingAutoFinishAttempted = true;
      this.finishDailyTraining();
    },
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap chat-whats flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
      'acl-whatsapp-mobile-scope': isMobileWhatsAppConversation,
      'acl-conversation-mobile-shell': isMobileViewport && currentChat.id,
    }"
    :data-mobile-chat-layout="
      isMobileWhatsAppConversation ? 'whatsapp' : undefined
    "
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="
        (isOnExpandedLayout || isMobileViewport) && !isInboxView
      "
      class="acl-conversation-mobile-header"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <div
      v-if="isDailyTrainingConversation"
      class="daily-training-banner flex flex-wrap items-center gap-3 border-y px-4 py-3 shadow-sm"
    >
      <span
        class="i-lucide-graduation-cap size-5 flex-shrink-0 text-amber-700"
        aria-hidden="true"
      />
      <div class="min-w-0 flex-1">
        <p class="m-0 text-sm font-semibold">{{ trainingBannerTitle }}</p>
        <p class="m-0 text-xs text-amber-900">
          {{ trainingBannerDescription }}
        </p>
      </div>
      <div
        class="daily-training-timer inline-flex min-w-[116px] items-center gap-2 rounded-lg px-3 py-2"
        :class="{ 'daily-training-timer--ready': dailyTrainingMinimumReached }"
        aria-live="polite"
      >
        <span
          class="i-lucide-clock-3 size-4 flex-shrink-0"
          aria-hidden="true"
        />
        <div>
          <p class="m-0 text-[11px] font-semibold uppercase leading-none">
            Tempo de treino
          </p>
          <p class="m-0 mt-1 font-mono text-sm font-bold leading-none">
            {{ dailyTrainingElapsedLabel }}
            <span
              v-if="dailyTrainingMinimumDuration"
              class="font-medium opacity-75"
            >
              / {{ dailyTrainingMinimumLabel }}
            </span>
          </p>
        </div>
      </div>
      <button
        type="button"
        class="daily-training-finish-button inline-flex h-9 items-center rounded-lg px-4 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="finishingDailyTraining"
        @click="finishDailyTraining(false)"
      >
        {{ trainingFinishLabel }}
      </button>
      <button
        v-if="canForceFinishDailyTraining"
        type="button"
        class="daily-training-force-finish-button inline-flex h-9 items-center rounded-lg border px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
        title="Ação exclusiva de lucas@skalacode.com"
        :disabled="finishingDailyTraining"
        @click="finishDailyTraining(true)"
      >
        Finalizar forçadamente (exclusivo Lucas)
      </button>
    </div>
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div
      v-show="!activeIndex"
      class="acl-conversation-mobile-body flex h-auto min-h-0 min-w-0 flex-1 flex-col m-0 overflow-hidden"
    >
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
    <!-- Reconnection is intentionally rendered only below the composer. -->
    <!-- <woot-modal
      v-model:show="reconnectModalOpen"
      :on-close="closeReconnectModal"
    >
      <woot-modal-header
        header-title="Reconectar WhatsApp"
        header-content="Use o WhatsApp do celular para escanear o QR Code desta instância existente."
      />
      <div class="px-6 pb-6 pt-2 text-n-slate-12">
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
          <div class="mb-3 flex items-start gap-3">
            <span
              class="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-700"
              aria-hidden="true"
            >
              <span class="i-lucide-refresh-cw size-5" />
            </span>
            <div class="min-w-0">
              <p class="m-0 text-sm font-semibold text-n-slate-12">
                Reconectando a instância existente
              </p>
              <p class="m-0 mt-1 text-sm text-n-slate-11">
                {{ reconnectStatusText }}
              </p>
            </div>
          </div>

          <div
            v-if="reconnectQrBase64"
            class="flex flex-col items-center gap-3"
          >
            <img
              :src="reconnectQrBase64"
              alt="QR Code para reconectar WhatsApp"
              class="h-64 w-64 rounded-lg border border-n-weak bg-white p-3"
            />
            <p class="m-0 text-center text-sm text-n-slate-11">
              Abra o WhatsApp no celular, toque em Dispositivos conectados e
              escaneie este QR Code.
            </p>
          </div>

          <div
            v-if="reconnectPairingCode"
            class="mt-4 rounded-lg bg-n-alpha-2 p-3 text-center"
          >
            <p class="m-0 text-xs font-semibold uppercase text-n-slate-10">
              Código de pareamento
            </p>
            <p
              class="m-0 mt-1 font-mono text-lg font-semibold tracking-wider text-n-slate-12"
            >
              {{ reconnectPairingCode }}
            </p>
          </div>

          <p
            v-if="reconnectError"
            class="m-0 mt-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {{ reconnectError }}
          </p>

          <div
            v-if="reconnecting && !reconnectQrBase64"
            class="mt-4 flex items-center justify-center gap-2 text-sm text-n-slate-11"
          >
            <span class="i-lucide-loader-circle size-4 animate-spin" />
            <span>Gerando QR Code...</span>
          </div>
        </div>

        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12"
            @click="closeReconnectModal"
          >
            Fechar
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="reconnecting"
            @click="reconnectOfflineInbox"
          >
            <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
            <span>Gerar novo QR</span>
          </button>
        </div>
      </div>
    </woot-modal> -->
  </div>
</template>

<style scoped>
.daily-training-banner {
  background: #fef3c7 !important;
  border-color: #f59e0b !important;
  color: #451a03 !important;
}

.daily-training-banner :deep(.text-amber-700),
.daily-training-banner :deep(.text-amber-900) {
  color: #78350f !important;
}

.daily-training-timer {
  background: #fde68a;
  border: 1px solid #f59e0b;
  color: #78350f;
}

.daily-training-timer--ready {
  background: #dcfce7;
  border-color: #22c55e;
  color: #14532d;
}

.daily-training-finish-button {
  background: #b45309 !important;
}

.daily-training-finish-button:hover {
  background: #92400e !important;
}
</style>
